(() => {
  if (window.UniEditorialV6) { window.UniEditorialV6.init(document); return; }
  const controllers = new WeakMap();
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = matchMedia('(hover: hover) and (pointer: fine)');
  const canMove = node => !reduced.matches && document.body?.dataset.motion !== 'none' && !node.closest('[data-motion="none"]');
  const all = (scope, selector) => [...(scope.matches?.(selector) ? [scope] : []), ...scope.querySelectorAll(selector)];
  const register = (element, setup) => {
    if (controllers.has(element)) return;
    const controller = new AbortController();
    controllers.set(element, controller);
    setup(controller.signal);
  };
  function actors(root, signal) {
    const buttons = [...root.querySelectorAll('[data-actor-select]')];
    const select = button => {
      buttons.forEach(other => {
        const active = other === button;
        other.setAttribute('aria-pressed', String(active));
        other.closest('.uni-partnership__actor')?.classList.toggle('is-actor-active', active);
      });
      root.style.setProperty('--uni-actor-position', String(buttons.indexOf(button)));
    };
    buttons.forEach(button => {
      const card = button.closest('.uni-partnership__actor');
      button.addEventListener('click', () => select(button), { signal });
      button.addEventListener('focus', () => select(button), { signal });
      card?.addEventListener('pointerenter', event => {
        if (finePointer.matches && event.pointerType === 'mouse') select(button);
      }, { signal });
    });
    if (buttons[0]) select(buttons[0]);
  }
  function method(root, signal) {
    const moments = [...root.querySelectorAll('[data-method-moment]')];
    if (!moments.length) return;
    const setMoment = moment => { root.dataset.methodState = moment; root.querySelectorAll('[data-method-select]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.methodSelect === moment))); };
    root.querySelectorAll('[data-method-select]').forEach(button => {
      const select = () => setMoment(button.dataset.methodSelect);
      button.addEventListener('click', select, { signal });
      button.addEventListener('focus', select, { signal });
      button.closest('[data-method-moment]')?.addEventListener('pointerenter', event => {
        if (finePointer.matches && event.pointerType === 'mouse') select();
      }, { signal });
    });
    const update = () => {
      if (!canMove(root)) { root.removeAttribute('data-method-state'); return; }
      const target = innerHeight * .61;
      let closest = moments[0], distance = Infinity;
      for (const moment of moments) {
        const rect = moment.getBoundingClientRect();
        const next = Math.abs(rect.top + rect.height * .5 - target);
        if (next < distance) { closest = moment; distance = next; }
      }
      setMoment(closest.dataset.methodMoment);
    };
    let inView = false, frame = 0;
    const schedule = () => { if (!inView || frame) return; frame = requestAnimationFrame(() => { frame = 0; update(); }); };
    const observer = new IntersectionObserver(entries => { inView = entries[0].isIntersecting; if (inView) update(); }, { rootMargin: '15% 0px' });
    observer.observe(root);
    window.addEventListener('scroll', schedule, { passive: true, signal });
    window.addEventListener('resize', schedule, { passive: true, signal });
    reduced.addEventListener('change', update, { signal });
    document.addEventListener('uni:motionchange', update, { signal });
    signal.addEventListener('abort', () => { observer.disconnect(); cancelAnimationFrame(frame); }, { once: true });
    update();
  }
  function reading(root, signal) {
    const body = root.querySelector('[data-reading-body]'), track = root.querySelector('[data-reading-track]');
    if (!body || !track) return;
    let frame = 0, visible = false;
    const update = () => {
      frame = 0;
      const rect = body.getBoundingClientRect();
      const long = rect.height > innerHeight * 1.6;
      track.hidden = !long || !canMove(root);
      if (track.hidden) return;
      const progress = Math.min(1, Math.max(0, (innerHeight - rect.top) / (rect.height + innerHeight)));
      track.style.setProperty('--uni-reading', progress.toFixed(4));
    };
    const schedule = () => { if (visible && !frame) frame = requestAnimationFrame(update); };
    const observer = new IntersectionObserver(entries => { visible = entries[0].isIntersecting; update(); });
    observer.observe(root);
    const resize = new ResizeObserver(update); resize.observe(body);
    window.addEventListener('scroll', schedule, { passive: true, signal });
    window.addEventListener('resize', update, { passive: true, signal });
    reduced.addEventListener('change', update, { signal });
    document.addEventListener('uni:motionchange', update, { signal });
    signal.addEventListener('abort', () => { observer.disconnect(); resize.disconnect(); cancelAnimationFrame(frame); }, { once: true });
    update();
  }
  const escapeICS = value => String(value || '').replace(/\\/g, '\\\\').replace(/\r?\n|\r/g, '\\n').replace(/;/g, '\\;').replace(/,/g, '\\,');
  const icsDate = date => date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}Z$/, 'Z');
  function foldLine(line) {
    const encoder = new TextEncoder();
    let output = '', row = '', bytes = 0;
    for (const char of line) {
      const size = encoder.encode(char).length;
      if (bytes + size > 74) { output += row + '\r\n'; row = ' '; bytes = 1; }
      row += char; bytes += size;
    }
    return output + row;
  }
  function readEvent(button) {
    const data = button.dataset;
    if (!data.calendarTitle || !/T\d{2}:\d{2}.*(?:Z|[+-]\d{2}:?\d{2})$/i.test(data.calendarStart || '')) return null;
    try { new Intl.DateTimeFormat('es-CO', { timeZone: data.calendarTimezone }).format(new Date()); } catch (_) { return null; }
    const start = new Date(data.calendarStart);
    if (!Number.isFinite(start.getTime())) return null;
    let end = null;
    if (data.calendarEnd) {
      if (!/T\d{2}:\d{2}.*(?:Z|[+-]\d{2}:?\d{2})$/i.test(data.calendarEnd)) return null;
      end = new Date(data.calendarEnd);
      if (!Number.isFinite(end.getTime()) || end <= start) return null;
    }
    return { data, start, end };
  }
  function calendar(button, signal) {
    if (!readEvent(button)) return;
    button.hidden = false;
    button.addEventListener('click', () => {
      const event = readEvent(button); if (!event) return;
      const { data, start, end } = event;
      const identity = (data.calendarId || 'evento').replace(/[^a-z0-9_-]/gi, '').slice(0, 120);
      let url = '';
      try { const candidate = new URL(data.calendarUrl, location.origin); if (['https:', 'http:'].includes(candidate.protocol)) url = candidate.href; } catch (_) {}
      const lines = ['BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Unisystem//Agenda institucional//ES', 'CALSCALE:GREGORIAN', 'METHOD:PUBLISH', 'BEGIN:VEVENT', `UID:${identity}-${start.getTime()}@${location.hostname}`, `DTSTAMP:${icsDate(new Date())}`, `DTSTART:${icsDate(start)}`];
      if (end) lines.push(`DTEND:${icsDate(end)}`);
      lines.push(`SUMMARY:${escapeICS(data.calendarTitle)}`);
      if (data.calendarDescription) lines.push(`DESCRIPTION:${escapeICS(data.calendarDescription)}`);
      if (data.calendarLocation) lines.push(`LOCATION:${escapeICS(data.calendarLocation)}`);
      if (url) lines.push(`URL:${escapeICS(url)}`);
      lines.push('END:VEVENT', 'END:VCALENDAR');
      const objectURL = URL.createObjectURL(new Blob([lines.map(foldLine).join('\r\n') + '\r\n'], { type: 'text/calendar;charset=utf-8' }));
      const anchor = document.createElement('a'); anchor.href = objectURL; anchor.download = `${identity || 'evento'}.ics`; document.body.append(anchor); anchor.click(); anchor.remove();
      setTimeout(() => URL.revokeObjectURL(objectURL), 1000);
    }, { signal });
  }
  function agenda(root, signal) {
    const cards = [...root.querySelectorAll('[data-agenda-event]')], rail = root.querySelector('[data-agenda-dates]');
    if (!rail || !cards.length) return;
    const validDay = value => /^\d{4}-\d{2}-\d{2}$/.test(value || '') && Number.isFinite(new Date(value + 'T12:00:00Z').getTime());
    const dates = [...new Set(cards.map(card => card.dataset.eventDay).filter(validDay))].sort();
    if (!dates.length) return;
    const months = [...new Set(dates.map(value => value.slice(0, 7)))];
    const dayFormatter = new Intl.DateTimeFormat(document.documentElement.lang || 'es', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
    const monthFormatter = new Intl.DateTimeFormat(document.documentElement.lang || 'es', { month: 'long', year: 'numeric', timeZone: 'UTC' });
    const weekFormatter = new Intl.DateTimeFormat(document.documentElement.lang || 'es', { weekday: 'short', timeZone: 'UTC' });
    const dayDate = day => new Date(day + 'T12:00:00Z');
    let selected = '', monthIndex = 0;
    cards.sort((a, b) => a.dataset.eventDay.localeCompare(b.dataset.eventDay)).forEach(card => card.parentElement.append(card));
    rail.setAttribute('role', 'group'); rail.setAttribute('aria-label', root.dataset.calendarLabel || '');
    function select(day) {
      selected = day;
      cards.forEach(card => { card.hidden = Boolean(day && card.dataset.eventDay !== day); });
      root.querySelectorAll('[data-agenda-day]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.agendaDay === day)));
    }
    const addDay = (day, text) => {
      const button = document.createElement('button'); button.type = 'button'; button.dataset.agendaDay = day; button.textContent = text; button.setAttribute('aria-pressed', String(day === selected));
      button.addEventListener('click', () => { select(day); if (day) { monthIndex = months.indexOf(day.slice(0, 7)); drawMonth(); } }, { signal }); rail.append(button);
    };
    addDay('', root.dataset.calendarAll || ''); dates.forEach(day => addDay(day, dayFormatter.format(dayDate(day))));
    const calendarRoot = document.createElement('div'); calendarRoot.className = 'uni-agenda-month'; calendarRoot.setAttribute('role', 'group'); calendarRoot.setAttribute('aria-label', root.dataset.calendarLabel || ''); rail.before(calendarRoot);
    function drawMonth() {
      calendarRoot.replaceChildren();
      const month = months[monthIndex], [year, number] = month.split('-').map(Number);
      const header = document.createElement('div'); header.className = 'uni-agenda-month__head';
      const nav = (direction) => {
        const button = document.createElement('button'); button.type = 'button'; button.className = 'uni-agenda-month__nav'; button.disabled = !months[monthIndex + direction];
        button.setAttribute('aria-label', monthFormatter.format(dayDate((months[monthIndex + direction] || month) + '-01')));
        const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg'); svg.setAttribute('viewBox', '0 0 24 24'); svg.setAttribute('width', '18'); svg.setAttribute('height', '18'); svg.setAttribute('aria-hidden', 'true');
        const path = document.createElementNS('http://www.w3.org/2000/svg', 'path'); path.setAttribute('d', direction < 0 ? 'm14 6-6 6 6 6' : 'm10 6 6 6-6 6'); path.setAttribute('fill', 'none'); path.setAttribute('stroke', 'currentColor'); path.setAttribute('stroke-width', '1.6'); svg.append(path); button.append(svg);
        button.addEventListener('click', () => { monthIndex += direction; drawMonth(); calendarRoot.querySelector(direction < 0 ? 'button' : '.uni-agenda-month__head button:last-child')?.focus(); }, { signal }); return button;
      };
      const title = document.createElement('span'); title.textContent = monthFormatter.format(dayDate(month + '-01')); header.append(nav(-1), title, nav(1)); calendarRoot.append(header);
      const grid = document.createElement('div'); grid.className = 'uni-agenda-month__grid';
      for (let index = 0; index < 7; index++) { const label = document.createElement('span'); label.textContent = weekFormatter.format(new Date(Date.UTC(2024, 0, 1 + index, 12))); grid.append(label); }
      const start = (new Date(Date.UTC(year, number - 1, 1)).getUTCDay() + 6) % 7;
      for (let i = 0; i < start; i++) { const blank = document.createElement('span'); blank.setAttribute('aria-hidden', 'true'); grid.append(blank); }
      const length = new Date(Date.UTC(year, number, 0)).getUTCDate();
      for (let i = 1; i <= length; i++) {
        const day = `${month}-${String(i).padStart(2, '0')}`, button = document.createElement('button'); button.type = 'button'; button.textContent = String(i); button.disabled = !dates.includes(day);
        button.setAttribute('aria-label', dayFormatter.format(dayDate(day)));
        if (!button.disabled) { button.dataset.agendaDay = day; button.dataset.hasEvents = ''; button.setAttribute('aria-pressed', String(day === selected)); button.addEventListener('click', () => select(day), { signal }); }
        grid.append(button);
      }
      calendarRoot.append(grid);
    }
    drawMonth(); rail.hidden = false;
    signal.addEventListener('abort', () => { calendarRoot.remove(); rail.replaceChildren(); rail.hidden = true; cards.forEach(card => { card.hidden = false; }); }, { once: true });
  }
  function arrival(root, signal) {
    const button = root.querySelector('[data-copy-address]'), status = root.querySelector('[data-arrival-status]');
    if (!button || !navigator.clipboard?.writeText) return;
    button.hidden = false;
    button.addEventListener('click', async () => {
      try { await navigator.clipboard.writeText(button.dataset.copyAddress); if (!signal.aborted && status) status.textContent = button.dataset.copiedLabel; } catch (_) { button.hidden = true; }
    }, { signal });
  }
  function init(scope) {
    for (const [selector, setup] of [['[data-partnership-actors]', actors], ['[data-method-story]', method], ['[data-editorial-reading]', reading], ['[data-calendar-save]', calendar], ['[data-event-agenda]', agenda], ['[data-arrival-guide]', arrival]]) all(scope, selector).forEach(element => register(element, signal => setup(element, signal)));
  }
  function unload(scope) { all(scope, '[data-partnership-actors],[data-method-story],[data-editorial-reading],[data-calendar-save],[data-event-agenda],[data-arrival-guide]').forEach(element => { controllers.get(element)?.abort(); controllers.delete(element); }); }
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => unload(event.target));
  window.UniEditorialV6 = { init };
  init(document);
})();
